import { match, P } from 'ts-pattern';
import { format, subDays } from 'date-fns';
import { pipe } from 'fp-ts/function';
import * as Opt from 'fp-ts/Option';
import { Ord as OrdDate } from 'fp-ts/Date';
import { isEmpty } from 'lodash';
import * as Ord from 'fp-ts/Ord';
import { convertToString, parseDate } from 'utils/datetime';
import { TimeSpanOption } from 'modules/intelligentInsights/brandAffinityInsights/types';
import { BrandAffinityLevel } from 'modules/domains';
import { localizeNumber } from 'utils/l10n/number';
import { getValidIntlLocale } from 'utils/l10n/locale';
import { DEFAULT_LOCALE } from 'utils/l10n/constants';
import { AVERAGE_SENDS_DIFFS } from 'modules/intelligentInsights/constants';
import { TimespanEnum } from 'modules/intelligentInsights/types';
import {
  FroInsightsData,
  MessageMediumByTimeData,
} from 'modules/intelligentInsights/frequencyOptimization/types';

export const toTimespanDisplay = (timespan: TimespanEnum) => timespan.toString();

export const IntelligentInsightsTimeSpanOptions: TimeSpanOption[] = [
  { label: 'Past 30 days', value: toTimespanDisplay(TimespanEnum.Thirty) },
  { label: 'Past 60 days', value: toTimespanDisplay(TimespanEnum.Sixty) },
  { label: 'Past 90 days', value: toTimespanDisplay(TimespanEnum.Ninety) },
];

export const asTimespanOption = (timespan: TimespanEnum) =>
  match(timespan)
    .with(TimespanEnum.Ninety, () => IntelligentInsightsTimeSpanOptions[2])
    .with(TimespanEnum.Sixty, () => IntelligentInsightsTimeSpanOptions[1])
    .with(TimespanEnum.Thirty, () => IntelligentInsightsTimeSpanOptions[0])
    .otherwise(() => IntelligentInsightsTimeSpanOptions[0]);

export const asTimespanEnum = (timespanDisplayOption: TimeSpanOption) =>
  match(timespanDisplayOption)
    .with(IntelligentInsightsTimeSpanOptions[0], () => TimespanEnum.Thirty)
    .with(IntelligentInsightsTimeSpanOptions[1], () => TimespanEnum.Sixty)
    .with(IntelligentInsightsTimeSpanOptions[2], () => TimespanEnum.Ninety)
    .otherwise(() => TimespanEnum.Thirty);

export const formatAsShortDate = (computedDate: string | Date) =>
  format(parseDate(computedDate), 'MMM d');
export const formatAsFullDate = (computedDate: string | Date) =>
  format(parseDate(computedDate), 'EEEE, MMM d, kk:mm');
export const formatAsLocalDate = (computedDate: string | Date) =>
  format(parseDate(computedDate), 'yyyy-MM-dd');
export const formatCSVFilenameDate = (computedDate: string | Date) =>
  format(parseDate(computedDate), 'yyyy-MM-d');
export const formatAsCommonDate = (computedDate: string | Date) =>
  format(parseDate(computedDate), 'MMM d, y');
export const formatAsCSVDate = (computedDate: string | Date) =>
  format(parseDate(computedDate), 'MM/dd/yyyy');

export const createCoreFetchVars = (timeSpan: TimespanEnum, compareDate = new Date()) => {
  const endDate = compareDate;
  const startDate = subDays(parseDate(endDate), timeSpan);
  return {
    timespan: timeSpan,
    startDate: formatAsLocalDate(startDate),
    endDate: formatAsLocalDate(endDate),
  };
};

export const getOtherBrandAffinityCohorts = (level: BrandAffinityLevel): BrandAffinityLevel[] => {
  return Object.values(BrandAffinityLevel).filter((l) => l !== level);
};

export const trimNumber = (source: number, decimalPlaces = 4) => {
  const asString = String(source);
  const [leadingInteger, quotient] = asString.split('.');

  return match({ leadingInteger, quotient })
    .with(
      // 1.2345
      {
        leadingInteger: P.when((leading) => leading !== '0' && leading !== '-0'),
        quotient: P.nonNullable,
      },
      () => {
        return Number(leadingInteger.concat('.').concat(quotient.slice(0, 2)));
      },
    )
    .with(
      // 1
      {
        leadingInteger: P.when((leading) => leading !== '0' && leading !== '-0'),
        quotient: P.nullish,
      },
      () => {
        return Number(leadingInteger);
      },
    )
    .with(
      // 0.12345
      {
        leadingInteger: P.when((leading) => leading === '0' || leading === '-0'),
        quotient: P.nonNullable,
      },
      () => {
        return Number(leadingInteger.concat('.').concat(quotient.slice(0, decimalPlaces)));
      },
    )
    .with(
      // 0
      {
        leadingInteger: P.when((leading) => leading === '0' || leading === '-0'),
        quotient: P.nullish,
      },
      () => 0,
    )
    .otherwise(() => 0);
};

/**
 @description Add + sign to positive numbers as strings
 @example +5
*/
export const addPositiveSign = (value: number, stringValue: string) =>
  value > 0 ? `+${stringValue}` : stringValue;

/**
 @description Format a number as a percentage points string, without post %
 @example 12.23
 */
export const formatPercentagePoints = (value: number) =>
  `${new Intl.NumberFormat('default', {
    style: 'percent',
    maximumFractionDigits: 2,
    // @ts-ignore
    roundingMode: 'trunc',
    useGrouping: false,
  }).format(value)}`.slice(0, -1);

/**
 @description Format a number as a percentage points string and sign, without post %  
 @example +12.23
 */
export const formatPercentWithSign = (value: number) =>
  addPositiveSign(value, `${formatPercentagePoints(value)}`);

/**
 @description Format a number as a count string, without sign
 @example 1234
 */
export const formatCount = (value: number) => localizeNumber(value || 0);

/**
 @description Format a number as a count string, with sign
 @example +1234 
 */
export const formatCountWithSign = (value: number) => addPositiveSign(value, formatCount(value));

/**
 @description Create a filename prefix for a CSV file based on the current date
 @example Example_file_name_2021-12-31.csv
 */
export const formatCsvFilenameWithDate = (fileNameFormatter: (input: string) => string) =>
  pipe(new Date(), convertToString, formatCSVFilenameDate, fileNameFormatter);

export const customLocalizeNumber = (value: number, fractionDigits = 1) => {
  return new Intl.NumberFormat(getValidIntlLocale(DEFAULT_LOCALE), {
    maximumFractionDigits: fractionDigits,
  }).format(value);
};

export const truncateNumber = (num: number, digits = 2) => {
  //  Truncatation test the number for a small fractional value
  if (Math.trunc(Math.abs(num)) > 0) {
    // Return the absolute value of the truncated number
    return Math.abs(Math.trunc(num * 100) / 100.0);
  } else {
    // Return absolute value of the formatted fractional number
    return Math.abs(Number(customLocalizeNumber(num, digits)));
  }
};

export const formatAvgSendsDiff = (holdSends: number, optimizedSends: number) => {
  const diff = holdSends - optimizedSends;
  const digits = Math.trunc(diff) === 0 ? 2 : 1;
  return match(diff)
    .when(
      (value) => truncateNumber(value) === 0,
      () => `${AVERAGE_SENDS_DIFFS.EQUAL}`,
    )
    .when(
      (value) => value > 0,
      (value) => `${customLocalizeNumber(value, digits)} ${AVERAGE_SENDS_DIFFS.FEWER}`,
    )
    .when(
      (value) => value < 0,
      (value) => `${customLocalizeNumber(Math.abs(value), digits)} ${AVERAGE_SENDS_DIFFS.MORE}`,
    )
    .otherwise((value) => `${customLocalizeNumber(value, digits)} ${AVERAGE_SENDS_DIFFS.FEWER}`);
};

export const getInsightsHistory = (froInsightsData: FroInsightsData) =>
  isEmpty(froInsightsData?.messageMediumByTimeData)
    ? Opt.none
    : Opt.some(froInsightsData.messageMediumByTimeData);

export const ordByDate = pipe(
  OrdDate,
  Ord.contramap((x: MessageMediumByTimeData) => parseDate(x.endDate)),
);
